import { QueryClientProvider } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Toaster } from "sonner";

import { AppShell } from "@/components/appshell/AppShell";
import { GlobalOverlays } from "@/components/shell/GlobalOverlays";
import { ChatLauncher } from "@/components/appshell/ChatLauncher";
import { FileTreePanel } from "@/components/files/FileTreePanel";
import { ProjectsPanel } from "@/components/projects/ProjectsPanel";
import { RightRail } from "@/components/rail/RightRail";
import type { AtlasSearchHandle } from "@/components/shell/CommandPalette";
import { BackupDialog } from "@/components/shell/BackupDialog";
import { Drawer } from "@/components/shell/Drawer";
import { AppErrorBoundary } from "@/components/feedback";
import { ErrorBoundary } from "@/components/shell/ErrorBoundary";
import { GlobalDropzone } from "@/components/shell/GlobalDropzone";
import { MobileAddSheet } from "@/components/shell/MobileAddSheet";
import { MobileDock } from "@/components/shell/MobileDock";
import { SignedOutScreen } from "@/components/shell/SignedOutScreen";
import { SmartExportDialog } from "@/components/shell/SmartExportDialog";
import { TokensDialog } from "@/components/shell/TokensDialog";
import { TopBar } from "@/components/shell/TopBar";
import { WebsiteDialog } from "@/components/shell/WebsiteDialog";
import { TooltipProvider } from "@/components/ui/overlays";
import { WorkspaceSurface } from "@/components/workspace/WorkspaceSurface";
import { useAuthGate } from "@/hooks/use-auth-gate";
import { useBackupReminder } from "@/hooks/use-backup-reminder";
import { useIsCompact, useIsTablet } from "@/hooks/use-media-query";
import { useBackGuard, useDeepLink } from "@/hooks/use-router";
import { queryClient } from "@/lib/api";
import { isPreview } from "@/lib/preview";
import { useGraph } from "@/stores/graph";
import { useShell } from "@/stores/shell";
import { useTabs } from "@/stores/tabs";

export default function App() {
  // The atlas search is a real combobox now: the TopBar bar IS the input and a
  // results dropdown drops beneath it (see shell/CommandPalette). The combobox
  // lives inside whichever TopBar (compact or desktop) is mounted; App holds an
  // imperative handle to it so ⌘K and the LeftBar "Search" tool can open the
  // results — no separate palette open-state to own.
  const searchRef = useRef<AtlasSearchHandle | null>(null);
  // Open the search results, expanding the top bar first when it's collapsed (⌘K
  // / LeftBar can fire while the bar is hidden). Both top-bar flags are set:
  // only the one for the mounted layout has any effect (they're separate, never
  // both live). `flushSync` commits the expansion synchronously so the combobox
  // is mounted (and its imperative handle registered) before we call it.
  const focusSearch = useCallback(() => {
    flushSync(() => useShell.setState({ topOpen: true, compactTopOpen: true }));
    searchRef.current?.openAndFocus();
  }, []);
  const websiteOpen = useShell((s) => s.websiteOpen);
  const [smartExportOpen, setSmartExportOpen] = useState(false);
  const [tokensOpen, setTokensOpen] = useState(false);
  const [backupOpen, setBackupOpen] = useState(false);
  useBackupReminder();

  // Auth gate: when a provider is configured and no one is signed in, the whole
  // studio is replaced by the dedicated signed-out screen (no top bar / right
  // rail — just an empty file-rail + the login pane). See use-auth-gate.ts.
  const auth = useAuthGate();

  // --- responsive shell (Obsidian-style on phones/tablets-portrait) ---------
  const compact = useIsCompact();
  const isTablet = useIsTablet();
  const reduceMotion = useReducedMotion();
  const leftOpen = useShell((s) => s.leftOpen);
  const rightPane = useShell((s) => s.rightPane);
  const addSheetOpen = useShell((s) => s.addSheetOpen);
  // Fullscreen ("borderless paper"): on compact there is no left spine to keep,
  // so ALL chrome hides — the surface's own exit button / Esc leaves the mode.
  const fullscreenChrome = useShell((s) => s.fullscreen);
  const selection = useGraph((s) => s.selection);
  const activeKind = useTabs((s) => {
    const t = s.tabs.find((x) => x.id === s.activeId);
    return t?.kind ?? "graph";
  });

  // On a TABLET reading/editing a document, the file explorer DOCKS to the left
  // and pushes the document right (both usable) instead of overlaying it as an
  // inert modal drawer. So while a doc surface is active, an open left panel is
  // a docked sidebar, not a modal.
  const isDocSurface = activeKind === "reader" || activeKind === "markdown";
  const filesDocked = isTablet && isDocSurface && leftOpen;

  // The shared Tools registry has to resolve "show the file panel" to whichever
  // shell is mounted, and only this component knows which that is.
  useEffect(() => {
    useShell.getState().setCompact(compact);
  }, [compact]);

  // When a compact overlay (drawer / add-sheet) is open it is a modal: the rest
  // of the shell must be inert so focus + screen-reader + pointer can't reach it.
  // The docked-files sidebar is NOT a modal, so it never makes the shell inert.
  const overlayOpen =
    compact && ((leftOpen && !filesDocked) || rightPane !== null || addSheetOpen);
  const shellRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (shellRef.current) shellRef.current.inert = overlayOpen;
  }, [overlayOpen]);

  // URL deep-linking (shareable docs/nodes/tabs) + system-Back closes an open
  // overlay instead of leaving the PWA.
  useDeepLink();
  useBackGuard(overlayOpen);

  // Selecting a node/edge pops the inspector drawer (the explicit "show me this")
  // — but ONLY while browsing the atlas. On a document/ask tab the document is
  // the focus, so a stray selection must never reopen a panel over it; this is
  // what kept "open a document → it's the only panel" from holding on compact.
  useEffect(() => {
    if (compact && selection && activeKind === "graph") useShell.getState().openRight("inspect");
  }, [compact, selection, activeKind]);
  // NOTE: dismissing drawers when a document/ask opens is done deterministically
  // inside the tabs store's open() (it calls useShell.closeAll() in the same
  // tick the tab activates). A post-paint effect here used to do it and kept
  // racing the inert drawer → the recurring "Read does nothing on mobile" bug.
  // Returning to desktop must never leave a fixed mobile overlay stranded.
  useEffect(() => {
    if (!compact) useShell.getState().closeAll();
  }, [compact]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        // Focus the search bar (expanding the top bar first if collapsed); the
        // bar's focus handler opens the results dropdown.
        focusSearch();
        return;
      }
      // Esc leaves fullscreen ("borderless paper") mode — unless something
      // else already claimed the keypress (an overlay closing, Excalidraw
      // cancelling a text edit) or the user is typing in a field.
      if (e.key === "Escape" && !e.defaultPrevented && useShell.getState().fullscreen) {
        const el = e.target as HTMLElement | null;
        const typing =
          !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
        if (!typing) {
          useShell.getState().exitFullscreen();
          return;
        }
      }
      // Del / Backspace deletes the selected node(s) — same as the Delete
      // button — unless the user is typing in a field.
      if (e.key === "Delete" || e.key === "Backspace") {
        const el = e.target as HTMLElement | null;
        const typing =
          !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
        if (typing) return;
        const g = useGraph.getState();
        if (g.selection?.kind === "node" || g.multiSelected.size > 0) {
          e.preventDefault();
          g.requestDelete();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusSearch]);

  // Still resolving provider config / restoring a session: a neutral obsidian
  // splash so neither the full app nor the login card flashes prematurely.
  if (auth.status === "loading") {
    return (
      <div className="grid h-full w-full place-items-center bg-background text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  // Signed out (a provider is configured, no user): render the login screen in
  // place of the whole shell. Query invalidation on sign-in lets the app load
  // under the new identity (the gate then flips to "signed-in").
  if (auth.status === "signed-out" && auth.accs) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <SignedOutScreen
            accs={auth.accs}
            onSignedIn={() => void queryClient.invalidateQueries()}
          />
          {isPreview() && <PreviewBadge />}
          {/* Bottom-right lands a toast directly ON the compact dock: it covers
              the tab bar and, because a sonner toast is a real element, it
              SWALLOWS the taps aimed at whatever is under it — "Tools" was
              unreachable for as long as the service-worker toast was up.
              On a phone the bottom edge belongs to navigation, so new toasts
              go to the top.
              The `offset` is not belt-and-braces: sonner stamps a POSITION ONTO
              EACH TOAST when it is created, and `lib/pwa.ts` fires its
              offline-ready toast from module scope, before this component has
              mounted — so that one keeps sonner's own bottom default whatever
              this prop says. The dock is kept clear for those by a CSS rule on
              the rendered container (see styles.css, "A toast must never land
              on the compact dock"); this prop only steers the ones created
              after mount. */}
          <Toaster
            position={compact ? "top-center" : "bottom-right"}
            toastOptions={{
              style: {
                background: "#10140F",
                border: "1px solid #1F2620",
                color: "#E7E5DA",
                fontSize: "13px",
              },
            }}
          />
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  // In preview a successful mock sign-in must NOT mount the real shell (it would
  // hammer the missing backend). Show a self-contained confirmation instead.
  if (isPreview() && auth.status === "signed-in") {
    return (
      <PreviewSignedIn email={auth.user?.email ?? null} onSignOut={() => void auth.accs?.signOut()} />
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <motion.div
          initial={{ opacity: reduceMotion ? 1 : 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: reduceMotion ? 0 : 0.5 }}
          className="flex h-full flex-col"
        >
          {/* Everything except the open overlay; goes inert while a modal drawer
              or add-sheet is up (the drawers below render OUTSIDE this region).
              Wrapped in a boundary so EITHER branch below (compact drawer shell
              or desktop AppShell) can never white-screen the whole studio — the
              desktop branch used to render bare, with no boundary above it. */}
          <div ref={shellRef} className="flex min-h-0 flex-1 flex-col">
            <AppErrorBoundary label="studio">
              {compact ? (
                // Compact (phone / portrait-tablet / touch-landscape-tablet): the
                // existing, battle-tested drawer-based shell — MobileDock + slide-in
                // Drawers below. The obsidian LeftBar/TopBar/RightBar chrome is a
                // desktop interaction pattern (edge open-buttons, a persistent rail)
                // that doesn't map onto a phone screen, so compact keeps its own
                // purpose-built responsive layout rather than adopting it.
                <ErrorBoundary label="workspace">
                  {!fullscreenChrome && (
                    <TopBar
                      onAddWebsite={() => useShell.getState().openWebsite()}
                      onSmartExport={() => setSmartExportOpen(true)}
                      onManageTokens={() => setTokensOpen(true)}
                      onManageBackups={() => setBackupOpen(true)}
                      searchRef={searchRef}
                    />
                  )}
                  {/* No legacy tab row here anymore: the Dockview tab strip
                      inside WorkspaceSurface is the single, shared tab UI on
                      every layout (it was unhidden on compact in workspace.css).
                      The docview's own <main> landmark (matching the desktop
                      AppShell's <main> — see obsidian-shell.spec.ts /
                      core-journey.spec.ts, which scope to `main` as the
                      studio's mount signal on every layout). */}
                  <main className="relative flex min-h-0 flex-1">
                    {/* Tablet + document: the files DOCK to the left and push the
                        document right (both usable), instead of an inert overlay. */}
                    {filesDocked && (
                      <div className="w-[clamp(200px,30vw,300px)] shrink-0 border-r border-border">
                        <FileTreePanel embedded onClose={() => useShell.getState().closeLeft()} />
                      </div>
                    )}
                    <WorkspaceSurface />
                    {/* Chat is exclusively the single ChatLauncher/ask panel now
                        (ReaderTab's own embedded per-document assistant was
                        retired) — mount the same launcher compact used to reach
                        only through that removed surface, so mobile still has a
                        chat entry point at all. */}
                    {!fullscreenChrome && <ChatLauncher />}
                  </main>
                  {!fullscreenChrome && <MobileDock />}
                </ErrorBoundary>
              ) : (
                // Desktop: the obsidian app shell (LeftBar | Center | RightBar,
                // collapsed Top/Right bars) wraps the always-mounted, borderless
                // Dockview workspace — the whole app IS the docview now, so there
                // is no more per-kind tab-switcher above this.
                <AppShell
                  onAddWebsite={() => useShell.getState().openWebsite()}
                  onSmartExport={() => setSmartExportOpen(true)}
                  onManageTokens={() => setTokensOpen(true)}
                  onManageBackups={() => setBackupOpen(true)}
                  searchRef={searchRef}
                >
                  <WorkspaceSurface />
                </AppShell>
              )}
            </AppErrorBoundary>
          </div>

          {/* Compact: the same panels slide in as drawers over the workspace. */}
          {compact && (
            <>
              <Drawer
                open={leftOpen && !filesDocked}
                side="left"
                label="Files"
                onClose={() => useShell.getState().closeLeft()}
              >
                <FileTreePanel embedded onClose={() => useShell.getState().closeLeft()} />
              </Drawer>
              <Drawer
                open={rightPane !== null}
                side="right"
                label={rightPane === "projects" ? "Projects" : "Inspector"}
                onClose={() => useShell.getState().closeRight()}
              >
                {rightPane === "projects" ? (
                  <ProjectsPanel embedded onClose={() => useShell.getState().closeRight()} />
                ) : (
                  <RightRail embedded onClose={() => useShell.getState().closeRight()} />
                )}
              </Drawer>
            </>
          )}
        </motion.div>

        {/* Mounted ABOVE the compact/desktop branch, so the Tools overlay, the
            shortcut sheet, the handwritten-note chooser and the upload picker
            exist on a phone and a tablet exactly as they do on a desktop.
            Three of the four previously lived inside AppShell, which is the
            desktop branch — so "New handwritten note…" in the mobile add-sheet
            raised an intent nothing was mounted to hear, and did nothing. */}
        <GlobalOverlays
          actions={{
            openPalette: focusSearch,
            addWebsite: () => useShell.getState().openWebsite(),
            smartExport: () => setSmartExportOpen(true),
            manageTokens: () => setTokensOpen(true),
            manageBackups: () => setBackupOpen(true),
          }}
        />

        <GlobalDropzone />
        <WebsiteDialog
          open={websiteOpen}
          onOpenChange={(v) => (v ? useShell.getState().openWebsite() : useShell.getState().closeWebsite())}
        />
        <SmartExportDialog open={smartExportOpen} onOpenChange={setSmartExportOpen} />
        <TokensDialog open={tokensOpen} onOpenChange={setTokensOpen} />
        <BackupDialog open={backupOpen} onOpenChange={setBackupOpen} />
        {compact && (
          <MobileAddSheet
            open={addSheetOpen}
            onClose={() => useShell.getState().closeAddSheet()}
            onAddWebsite={() => useShell.getState().openWebsite()}
          />
        )}
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#10140F",
              border: "1px solid #1F2620",
              color: "#E7E5DA",
              fontSize: "13px",
            },
          }}
        />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

/** Dev-preview only (`?preview`): a small badge explaining the mock, with the
 * special addresses that drive each login state. */
function PreviewBadge() {
  return (
    <div className="pointer-events-none fixed bottom-3 left-1/2 z-50 -translate-x-1/2 rounded-md bg-card/90 px-3 py-1.5 text-center text-[11px] leading-relaxed text-muted-foreground shadow-lg backdrop-blur">
      <span className="font-medium text-foreground">UI preview</span> — mock auth, no backend.
      Try emails containing <code className="text-primary">fail</code>,{" "}
      <code className="text-primary">hang</code>, or <code className="text-primary">verify</code>.
    </div>
  );
}

/** Dev-preview only: stand-in for the atlas after a successful mock sign-in, so
 * the real (backend-dependent) shell never mounts under `?preview`. */
function PreviewSignedIn({ email, onSignOut }: { email: string | null; onSignOut: () => void }) {
  return (
    <div className="grid h-full w-full place-items-center bg-background text-foreground">
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        <p className="font-display text-xl">Signed in{email ? ` as ${email}` : ""}</p>
        <p className="text-sm text-muted-foreground">
          This is the UI preview. In the real app your atlas, files, and AI assistant load here.
        </p>
        <button
          type="button"
          onClick={onSignOut}
          className="mt-1 rounded-md bg-secondary px-3 py-1.5 text-sm text-secondary-foreground hover:bg-accent"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
